import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    AccountSummary,
    EquityPosition,
    EquityOrderResult,
    MarketOrderRequest,
    CurrentTickerRating,
    LatestPriceDto,
    Instrument,
} from '../../api';
import {
    Alert,
    Autosuggest,
    Box,
    Button,
    Container,
    FormField,
    Grid,
    Header,
    Input,
    Modal,
    SpaceBetween,
    StatusIndicator,
    Table,
    Tabs,
    Toggle,
} from '@cloudscape-design/components';
import { trading212PublicApi, seekingAlphaApi, marketDataApi } from '../../client/clients';
import { RatingBadge, FactorGradeChips, AnalystDistributionBar } from './RatingCells';
import { LatestPriceCell } from './LatestPriceCell';
import { shortTicker } from '../../util/ratings';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { formatMoney } from '../../util/formatMoney';
import { useFlashbar } from '../../context/FlashbarContext';
import { classifyError, alertTypeForKind } from '../../util/errorClassifier';

// ---------------------------------------------------------------------------
// Retry helper — silent backoff on rate-limit; only surface error after giving up
// ---------------------------------------------------------------------------

const sleep = (ms: number) => new Promise<void>(res => setTimeout(res, ms));

async function withRetry<T>(fn: () => Promise<T>, attempts = 3, backoffMs = 2000): Promise<T> {
    for (let i = 0; i < attempts; i++) {
        try {
            return await fn();
        } catch (err: unknown) {
            if (classifyError(err).kind !== 'rate-limit' || i === attempts - 1) throw err;
            await sleep(backoffMs * (i + 1));
        }
    }
    throw new Error('unreachable');
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type OrderSide = 'buy' | 'sell';

interface PositionModalState {
    position: EquityPosition;
    side: OrderSide;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Derive the instrument's native currency from a Trading212 ticker suffix. */
function instrumentCurrency(ticker: string | null | undefined): string {
    if (!ticker) return 'USD';
    if (ticker.includes('_US_')) return 'USD';
    if (ticker.includes('_GB_') || ticker.includes('_GBEX_')) return 'GBP';
    return 'USD';
}

const pctDiff = (current: number, avg: number): string => {
    if (!avg) return '—';
    const diff = ((current - avg) / avg) * 100;
    return `${diff >= 0 ? '+' : ''}${diff.toFixed(2)}%`;
};

const pnlColor = (val: number | undefined): string => {
    if (val === undefined || val === null) return '';
    return val >= 0 ? '#2ea597' : '#d13212';
};

export const computeQuantityFromValue = (value: number, price: number): number => {
    if (!price || price <= 0) return 0;
    return Math.floor((value / price) * 100) / 100;
};

/**
 * Whether a price (expected in account currency / GBP) can be trusted to size an
 * order by value. The backend nulls out currentPriceInAccountCurrency whenever it
 * can't produce a reliable GBP price, so by-value sizing must be refused in that
 * case rather than falling back to the instrument-currency price.
 */
export const isUsableGbpPrice = (price: number | null | undefined): price is number =>
    price != null && Number.isFinite(price) && price > 0;

const ACTIVE_ORDER_STATUSES = ['PENDING', 'QUEUED', 'WORKING'];

// ---------------------------------------------------------------------------
// Sub-component: metric tile used in the account summary grid
// ---------------------------------------------------------------------------

interface MetricTileProps {
    label: string;
    value: string;
    colorValue?: number;
}

const MetricTile: React.FC<MetricTileProps> = ({ label, value, colorValue }) => (
    <Box textAlign="center">
        <Box variant="small" color="text-body-secondary">{label}</Box>
        <Box
            variant="h3"
            fontWeight="bold"
            padding={{ top: 'xxs' }}
            color={colorValue !== undefined ? (colorValue >= 0 ? 'text-status-success' : 'text-status-error') : undefined}
        >
            {value}
        </Box>
    </Box>
);

// ---------------------------------------------------------------------------
// Account selection (Primary vs Ina) — persisted in localStorage
// ---------------------------------------------------------------------------

type AccountId = 'primary' | 'ina';
const LS_KEY = 't212.account';

function loadInitialAccount(): AccountId {
    try {
        const raw = window.localStorage.getItem(LS_KEY);
        return raw === 'ina' ? 'ina' : 'primary';
    } catch {
        return 'primary';
    }
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const Trading212IsaPage: React.FC = () => {
    useDocumentTitle('Trading 212 ISA');
    const flashbar = useFlashbar();

    // ---- account selection ----
    const [selectedAccount, setSelectedAccount] = useState<AccountId>(loadInitialAccount);

    useEffect(() => {
        try { window.localStorage.setItem(LS_KEY, selectedAccount); } catch { /* ignore */ }
    }, [selectedAccount]);

    // ---- data state ----
    const [accountSummary, setAccountSummary] = useState<AccountSummary | null>(null);
    const [positions, setPositions] = useState<EquityPosition[]>([]);
    const [orders, setOrders] = useState<EquityOrderResult[]>([]);
    const [ratings, setRatings] = useState<Record<string, CurrentTickerRating>>({});
    const [ratingsLoading, setRatingsLoading] = useState(false);
    const [latestPrices, setLatestPrices] = useState<Record<string, LatestPriceDto>>({});
    const [latestPricesLoading, setLatestPricesLoading] = useState(false);

    // ---- loading state ----
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [positionsLoading, setPositionsLoading] = useState(false);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [orderPlacing, setOrderPlacing] = useState(false);
    const [cancellingOrderId, setCancellingOrderId] = useState<number | null>(null);

    // ---- error state ----
    const [summaryError, setSummaryError] = useState('');
    const [positionsError, setPositionsError] = useState('');

    // ---- modal state ----
    const [positionModal, setPositionModal] = useState<PositionModalState | null>(null);
    const [modalQuantityInput, setModalQuantityInput] = useState('');
    const [modalValueInput, setModalValueInput] = useState('');
    const [modalByValue, setModalByValue] = useState(true); // buy modal defaults to value entry

    // ---- manual order entry state (search → select → quantity-only order) ----
    // The text currently in the Autosuggest field (free text or the chosen value).
    const [manualTicker, setManualTicker] = useState('');
    // Quantity (shares) to order — manual entry is quantity-only because T212's
    // market endpoint only accepts quantity and we have no price for arbitrary
    // (non-held) searched instruments to convert a value to shares.
    const [manualInput, setManualInput] = useState('');
    const [manualError, setManualError] = useState('');
    const [manualConfirmVisible, setManualConfirmVisible] = useState(false);
    const [manualPendingRequest, setManualPendingRequest] = useState<MarketOrderRequest | null>(null);

    // Instrument search results + the currently selected instrument.
    const [instrumentOptions, setInstrumentOptions] = useState<Instrument[]>([]);
    const [instrumentSearchLoading, setInstrumentSearchLoading] = useState(false);
    const [selectedInstrument, setSelectedInstrument] = useState<Instrument | null>(null);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const searchSeqRef = useRef(0);

    // ---------------------------------------------------------------------------
    // Data loading
    // ---------------------------------------------------------------------------

    const loadAccountSummary = useCallback(async () => {
        setSummaryLoading(true);
        setSummaryError('');
        try {
            const resp = await withRetry(() => trading212PublicApi.getAccountSummary(selectedAccount));
            setAccountSummary(resp.data);
        } catch (err) {
            const c = classifyError(err);
            setSummaryError(c.userMessage);
        } finally {
            setSummaryLoading(false);
        }
    }, [selectedAccount]);

    const loadPositions = useCallback(async () => {
        setPositionsLoading(true);
        setPositionsError('');
        try {
            const resp = await withRetry(() => trading212PublicApi.getPositions(selectedAccount));
            const sorted = [...(resp.data ?? [])].sort((a, b) => {
                // Prefer GBP market value for sorting; fall back to native price × qty
                const mv = (p: EquityPosition) =>
                    p.currentPriceInAccountCurrency != null
                        ? (p.currentPriceInAccountCurrency ?? 0) * (p.quantity ?? 0)
                        : (p.currentPrice ?? 0) * (p.quantity ?? 0);
                return mv(b) - mv(a);
            });
            setPositions(sorted);
        } catch (err) {
            const c = classifyError(err);
            setPositionsError(c.userMessage);
        } finally {
            setPositionsLoading(false);
        }
    }, [selectedAccount]);

    const loadOrders = useCallback(async () => {
        setOrdersLoading(true);
        try {
            const resp = await withRetry(() => trading212PublicApi.getOrders(selectedAccount));
            setOrders(resp.data ?? []);
        } catch (err) {
            const c = classifyError(err);
            flashbar.push({ type: 'error', header: 'Failed to load orders', content: c.userMessage });
        } finally {
            setOrdersLoading(false);
        }
    }, [flashbar, selectedAccount]);

    const refreshAll = useCallback(() => {
        loadAccountSummary();
        loadPositions();
        loadOrders();
    }, [loadAccountSummary, loadPositions, loadOrders]);

    useEffect(() => {
        refreshAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAccount]);

    // Fetch current SeekingAlpha ratings for the displayed tickers (one batched, server-cached call).
    useEffect(() => {
        const shortTickers = Array.from(new Set(
            positions
                .map(p => shortTicker(p.ticker))
                .filter(t => t.length > 0)
        ));
        if (shortTickers.length === 0) {
            setRatings({});
            return;
        }
        let cancelled = false;
        setRatingsLoading(true);
        seekingAlphaApi.getCurrentRatings(shortTickers.join(','))
            .then(resp => {
                if (cancelled) return;
                const map: Record<string, CurrentTickerRating> = {};
                (resp.data.data ?? []).forEach(r => {
                    if (r.ticker) map[r.ticker] = r;
                });
                setRatings(map);
            })
            .catch(() => {
                if (!cancelled) setRatings({}); // ratings are non-critical; leave cells as —
            })
            .finally(() => {
                if (!cancelled) setRatingsLoading(false);
            });
        return () => { cancelled = true; };
    }, [positions]);

    // Fetch latest (extended-hours-aware) prices for the displayed tickers (one batched call).
    useEffect(() => {
        const shortTickers = Array.from(new Set(
            positions
                .map(p => shortTicker(p.ticker))
                .filter(t => t.length > 0)
        ));
        if (shortTickers.length === 0) {
            setLatestPrices({});
            return;
        }
        let cancelled = false;
        setLatestPricesLoading(true);
        marketDataApi.getLatestPrices(shortTickers.join(','))
            .then(resp => {
                if (cancelled) return;
                const map: Record<string, LatestPriceDto> = {};
                (resp.data.data ?? []).forEach(d => {
                    if (d.ticker) map[d.ticker] = d;
                });
                setLatestPrices(map);
            })
            .catch(() => {
                if (!cancelled) setLatestPrices({}); // non-critical; leave cells as —, never break the table
            })
            .finally(() => {
                if (!cancelled) setLatestPricesLoading(false);
            });
        return () => { cancelled = true; };
    }, [positions]);

    // ---------------------------------------------------------------------------
    // Derived values
    // ---------------------------------------------------------------------------

    const availableToTrade = accountSummary?.cash?.availableToTrade ?? 0;
    const currency = accountSummary?.currency ?? 'GBP';

    const activeOrders = orders.filter(
        o => o.status && ACTIVE_ORDER_STATUSES.includes(o.status.toUpperCase())
    );

    // ---------------------------------------------------------------------------
    // Position modal helpers
    // ---------------------------------------------------------------------------

    const openBuyModal = (pos: EquityPosition) => {
        setPositionModal({ position: pos, side: 'buy' });
        setModalValueInput('');
        setModalQuantityInput('');
        setModalByValue(true);
    };

    const openSellModal = (pos: EquityPosition) => {
        setPositionModal({ position: pos, side: 'sell' });
        setModalQuantityInput('');
        setModalValueInput('');
        setModalByValue(false);
    };

    const closePositionModal = () => {
        if (orderPlacing) return;
        setPositionModal(null);
    };

    const setModalFraction = (fraction: number) => {
        if (!positionModal) return;
        if (positionModal.side === 'buy') {
            // fraction of availableToTrade
            const val = Math.round(availableToTrade * fraction * 100) / 100;
            setModalValueInput(val.toString());
            setModalByValue(true);
        } else if (modalByValue) {
            // sell by value: fraction of current market value in account currency.
            // Only the account-currency (GBP) price is trustworthy here — never fall
            // back to the instrument-currency price, which would mis-size the order.
            const avail = positionModal.position.quantityAvailableForTrading ?? 0;
            const price = positionModal.position.currentPriceInAccountCurrency;
            if (!isUsableGbpPrice(price)) {
                flashbar.push({
                    type: 'error',
                    header: 'Order failed',
                    content:
                        "Couldn't determine a GBP price for this instrument — switch to entering a share quantity.",
                });
                return;
            }
            const val = Math.round(avail * price * fraction * 100) / 100;
            setModalValueInput(val.toString());
        } else {
            // fraction of quantityAvailableForTrading
            const avail = positionModal.position.quantityAvailableForTrading ?? 0;
            const qty = Math.floor(avail * fraction * 100) / 100;
            setModalQuantityInput(qty.toString());
        }
    };

    const handleConfirmPositionOrder = async () => {
        if (!positionModal) return;
        const { position, side } = positionModal;
        // Value inputs are in account currency (GBP), so value→quantity conversion must
        // divide by a GBP price. The backend sets currentPriceInAccountCurrency to null
        // whenever it can't produce a reliable GBP price (FX unavailable / unsupported
        // currency). We deliberately do NOT fall back to currentPrice (instrument
        // currency, e.g. USD) — that would silently mis-size the order.
        const price = position.currentPriceInAccountCurrency;
        const hasGbpPrice = isUsableGbpPrice(price);
        const noPriceError = () =>
            flashbar.push({
                type: 'error',
                header: 'Order failed',
                content:
                    "Couldn't determine a GBP price for this instrument — switch to entering a share quantity.",
            });

        // Trading 212's market order endpoint only accepts `quantity` — sending
        // `valueInAccountCurrency` returns 400 "Invalid payload". So for buy-by-value
        // we convert the account-currency value to a quantity here using the
        // account-currency price (no FX needed; both sides are in account currency).
        let orderPayload: { ticker?: string; quantity?: number };

        if (side === 'buy') {
            if (modalByValue) {
                const val = parseFloat(modalValueInput);
                if (isNaN(val) || val <= 0) return;
                if (!hasGbpPrice) {
                    noPriceError();
                    return;
                }
                const quantity = computeQuantityFromValue(val, price);
                if (quantity <= 0) {
                    flashbar.push({
                        type: 'error',
                        header: 'Order failed',
                        content: `Value ${formatMoney(val, currency)} is too small to buy a share at the current price.`,
                    });
                    return;
                }
                orderPayload = { ticker: position.ticker, quantity };
            } else {
                const quantity = parseFloat(modalQuantityInput);
                if (isNaN(quantity) || quantity <= 0) return;
                orderPayload = { ticker: position.ticker, quantity };
            }
        } else {
            let quantity: number;
            if (modalByValue) {
                const val = parseFloat(modalValueInput);
                if (isNaN(val) || val <= 0) return;
                if (!hasGbpPrice) {
                    noPriceError();
                    return;
                }
                // Use the same flooring conversion as the confirm modal display so the
                // quantity shown to the user is exactly the quantity we send.
                quantity = computeQuantityFromValue(val, price);
                if (quantity <= 0) return;
            } else {
                quantity = parseFloat(modalQuantityInput);
                if (isNaN(quantity) || quantity <= 0) return;
            }
            orderPayload = { ticker: position.ticker, quantity: -quantity };
        }

        setOrderPlacing(true);
        try {
            await trading212PublicApi.placeMarketOrder(orderPayload, selectedAccount);
            const detail = `qty ${Math.abs(orderPayload.quantity ?? 0).toFixed(4)}`;
            flashbar.push({
                type: 'success',
                header: side === 'buy' ? 'Buy order placed' : 'Sell order placed',
                content: `${position.ticker}: ${detail}`,
            });
            setPositionModal(null);
            refreshAll();
        } catch (err) {
            const c = classifyError(err);
            flashbar.push({ type: 'error', header: 'Order failed', content: c.userMessage });
        } finally {
            setOrderPlacing(false);
        }
    };

    // ---------------------------------------------------------------------------
    // Cancel order
    // ---------------------------------------------------------------------------

    const handleCancelOrder = async (orderId: number) => {
        setCancellingOrderId(orderId);
        try {
            await trading212PublicApi.cancelOrder(orderId, selectedAccount);
            flashbar.push({ type: 'success', header: 'Order cancelled', content: `Order #${orderId} cancelled.` });
            loadOrders();
        } catch (err) {
            const c = classifyError(err);
            flashbar.push({ type: 'error', header: 'Cancel failed', content: c.userMessage });
        } finally {
            setCancellingOrderId(null);
        }
    };

    // ---------------------------------------------------------------------------
    // Manual order entry
    // ---------------------------------------------------------------------------

    // Debounced instrument search (min 2 chars). Maps the typed query to
    // Trading 212 instruments via the public-API search endpoint. Failures are
    // swallowed (no results, never crash).
    const runInstrumentSearch = useCallback((query: string) => {
        const trimmed = query.trim();
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        if (trimmed.length < 2) {
            setInstrumentOptions([]);
            setInstrumentSearchLoading(false);
            return;
        }
        setInstrumentSearchLoading(true);
        const seq = ++searchSeqRef.current;
        searchDebounceRef.current = setTimeout(async () => {
            try {
                const resp = await trading212PublicApi.getInstrumentSearch(trimmed, 20, selectedAccount);
                if (seq !== searchSeqRef.current) return; // a newer search superseded this one
                setInstrumentOptions(resp.data ?? []);
            } catch {
                if (seq !== searchSeqRef.current) return;
                setInstrumentOptions([]); // search failed — show no matches, never crash
            } finally {
                if (seq === searchSeqRef.current) setInstrumentSearchLoading(false);
            }
        }, 250);
    }, [selectedAccount]);

    // Clear any pending debounce on unmount.
    useEffect(() => () => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    }, []);

    const handleManualPlaceOrder = () => {
        setManualError('');
        if (!selectedInstrument?.ticker) {
            setManualError('Please search for and select an instrument.');
            return;
        }
        if (!manualInput.trim() || isNaN(Number(manualInput))) {
            setManualError('Please enter a valid quantity.');
            return;
        }
        const quantity = parseFloat(manualInput);
        if (quantity <= 0) {
            setManualError('Quantity must be greater than zero.');
            return;
        }
        const minQty = selectedInstrument.minTradeQuantity;
        if (minQty != null && quantity < minQty) {
            setManualError(`Minimum trade quantity for ${selectedInstrument.shortName ?? selectedInstrument.ticker} is ${minQty}.`);
            return;
        }
        setManualPendingRequest({ ticker: selectedInstrument.ticker, quantity });
        setManualConfirmVisible(true);
    };

    const handleManualConfirm = async () => {
        if (!manualPendingRequest) return;
        setOrderPlacing(true);
        setManualConfirmVisible(false);
        try {
            await trading212PublicApi.placeMarketOrder(manualPendingRequest, selectedAccount);
            const qty = manualPendingRequest.quantity ?? 0;
            const side = qty >= 0 ? 'Buy' : 'Sell';
            const detail = `qty ${Math.abs(qty).toFixed(4)}`;
            flashbar.push({
                type: 'success',
                header: `${side} order placed`,
                content: `${manualPendingRequest.ticker}: ${detail}`,
            });
            setManualInput('');
            setManualTicker('');
            setSelectedInstrument(null);
            setInstrumentOptions([]);
            setManualPendingRequest(null);
            refreshAll();
        } catch (err) {
            const c = classifyError(err);
            flashbar.push({ type: 'error', header: 'Order failed', content: c.userMessage });
        } finally {
            setOrderPlacing(false);
        }
    };

    // ---------------------------------------------------------------------------
    // Render helpers
    // ---------------------------------------------------------------------------

    const renderSummaryError = () => {
        if (!summaryError) return null;
        const c = classifyError(new Error(summaryError), { onRetry: loadAccountSummary });
        return (
            <Alert
                type={alertTypeForKind(c.kind)}
                header={c.userMessage}
                dismissible
                onDismiss={() => setSummaryError('')}
                action={
                    c.suggestedAction ? (
                        <Button onClick={c.suggestedAction.onClick} loading={summaryLoading}>
                            {c.suggestedAction.label}
                        </Button>
                    ) : undefined
                }
            />
        );
    };

    // ---------------------------------------------------------------------------
    // Render — Account Summary panel
    // ---------------------------------------------------------------------------

    const renderAccountSummary = () => (
        <Container
            header={
                <Header
                    variant="h2"
                    actions={
                        <Button
                            iconName="refresh"
                            variant="icon"
                            ariaLabel="Refresh account summary"
                            loading={summaryLoading}
                            onClick={loadAccountSummary}
                        />
                    }
                >
                    Account Summary
                </Header>
            }
        >
            {renderSummaryError()}
            {summaryLoading && !accountSummary && (
                <Box textAlign="center" color="text-body-secondary">Loading account data…</Box>
            )}
            {accountSummary && (
                <Grid
                    gridDefinition={[
                        { colspan: { default: 6, xs: 4, s: 3, m: 2 } },
                        { colspan: { default: 6, xs: 4, s: 3, m: 2 } },
                        { colspan: { default: 6, xs: 4, s: 3, m: 2 } },
                        { colspan: { default: 6, xs: 4, s: 3, m: 2 } },
                        { colspan: { default: 6, xs: 4, s: 3, m: 2 } },
                        { colspan: { default: 6, xs: 4, s: 3, m: 2 } },
                        { colspan: { default: 6, xs: 4, s: 3, m: 2 } },
                    ]}
                >
                    <MetricTile
                        label="Total Value"
                        value={formatMoney(accountSummary.totalValue, currency)}
                    />
                    <MetricTile
                        label="Available to Trade"
                        value={formatMoney(accountSummary.cash?.availableToTrade, currency)}
                    />
                    <MetricTile
                        label="In Pies"
                        value={formatMoney(accountSummary.cash?.inPies, currency)}
                    />
                    <MetricTile
                        label="Reserved for Orders"
                        value={formatMoney(accountSummary.cash?.reservedForOrders, currency)}
                    />
                    <MetricTile
                        label="Investment Value"
                        value={formatMoney(accountSummary.investments?.currentValue, currency)}
                    />
                    <MetricTile
                        label="Unrealized P&L"
                        value={formatMoney(accountSummary.investments?.unrealizedProfitLoss, currency)}
                        colorValue={accountSummary.investments?.unrealizedProfitLoss}
                    />
                    <MetricTile
                        label="Realized P&L"
                        value={formatMoney(accountSummary.investments?.realizedProfitLoss, currency)}
                        colorValue={accountSummary.investments?.realizedProfitLoss}
                    />
                </Grid>
            )}
        </Container>
    );

    // ---------------------------------------------------------------------------
    // Render — Portfolio table (compact, sorted by market value desc)
    // ---------------------------------------------------------------------------

    const renderPortfolio = () => (
        <SpaceBetween size="s">
            {positionsError && (
                <Alert
                    type="error"
                    header={positionsError}
                    dismissible
                    onDismiss={() => setPositionsError('')}
                />
            )}
            <Table
                header={
                    <Header
                        variant="h2"
                        counter={positions.length > 0 ? `(${positions.length})` : undefined}
                        actions={
                            <Button
                                iconName="refresh"
                                variant="icon"
                                ariaLabel="Refresh positions"
                                loading={positionsLoading}
                                onClick={loadPositions}
                            />
                        }
                    >
                        Portfolio
                    </Header>
                }
                items={positions}
                loading={positionsLoading}
                loadingText="Loading positions…"
                trackBy="ticker"
                empty={<Box textAlign="center" color="text-body-secondary">No open positions found.</Box>}
            columnDefinitions={[
                {
                    id: 'ticker',
                    header: 'Ticker',
                    cell: item => item.ticker
                        ? <Box fontWeight="bold">{item.ticker.split('_')[0]}</Box>
                        : <Box color="text-body-secondary">—</Box>,
                    width: 90,
                },
                {
                    id: 'value',
                    header: `Value (${currency})`,
                    cell: item => {
                        const posCurrency = item.currentPriceInAccountCurrency != null
                            ? currency : instrumentCurrency(item.ticker);
                        const mv = item.currentPriceInAccountCurrency != null
                            ? (item.currentPriceInAccountCurrency ?? 0) * (item.quantity ?? 0)
                            : (item.currentPrice ?? 0) * (item.quantity ?? 0);
                        return formatMoney(mv, posCurrency);
                    },
                    width: 120,
                },
                {
                    id: 'currentPrice',
                    header: 'Price',
                    cell: item => formatMoney(item.currentPrice, instrumentCurrency(item.ticker)),
                    width: 110,
                },
                {
                    id: 'last',
                    header: 'Last',
                    cell: item => {
                        const d = latestPrices[shortTicker(item.ticker)];
                        return <LatestPriceCell dto={d} loading={latestPricesLoading && !d} />;
                    },
                    width: 110,
                },
                {
                    id: 'avgPrice',
                    header: 'Avg Cost',
                    cell: item => formatMoney(item.averagePricePaid, instrumentCurrency(item.ticker)),
                    width: 120,
                },
                {
                    id: 'change',
                    header: '% Chg',
                    cell: item => {
                        const cur = item.currentPriceInAccountCurrency ?? item.currentPrice ?? 0;
                        const avg = item.averagePriceInAccountCurrency ?? item.averagePricePaid ?? 0;
                        const diff = pctDiff(cur, avg);
                        const up = cur >= avg;
                        return <span style={{ color: pnlColor(up ? 1 : -1) }}>{diff}</span>;
                    },
                    width: 80,
                },
                {
                    id: 'pnl',
                    header: `P&L (${currency})`,
                    cell: item => {
                        if (item.ppl != null) {
                            return <span style={{ color: pnlColor(item.ppl) }}>{formatMoney(item.ppl, currency)}</span>;
                        }
                        const posCurrency = instrumentCurrency(item.ticker);
                        const pnl = ((item.currentPrice ?? 0) - (item.averagePricePaid ?? 0)) * (item.quantity ?? 0);
                        return <span style={{ color: pnlColor(pnl) }}>{formatMoney(pnl, posCurrency)}</span>;
                    },
                    width: 110,
                },
                {
                    id: 'quant',
                    header: 'Quant',
                    cell: item => {
                        const r = ratings[shortTicker(item.ticker)];
                        return <RatingBadge value={r?.ratings?.quantRating} loading={ratingsLoading && !r} />;
                    },
                    width: 80,
                },
                {
                    id: 'authors',
                    header: 'Authors',
                    cell: item => {
                        const r = ratings[shortTicker(item.ticker)];
                        return <RatingBadge value={r?.ratings?.authorsRating} loading={ratingsLoading && !r} />;
                    },
                    width: 80,
                },
                {
                    id: 'analysts',
                    header: 'Analysts',
                    cell: item => {
                        const r = ratings[shortTicker(item.ticker)];
                        return <AnalystDistributionBar ratings={r?.ratings} loading={ratingsLoading && !r} />;
                    },
                    width: 140,
                },
                {
                    id: 'wallst',
                    header: 'Wall St',
                    cell: item => {
                        const r = ratings[shortTicker(item.ticker)];
                        return <RatingBadge value={r?.ratings?.sellSideRating} loading={ratingsLoading && !r} />;
                    },
                    width: 80,
                },
                {
                    id: 'grades',
                    header: 'Factor Grades',
                    cell: item => {
                        const r = ratings[shortTicker(item.ticker)];
                        return <FactorGradeChips ratings={r?.ratings} loading={ratingsLoading && !r} />;
                    },
                    width: 180,
                },
                {
                    id: 'qty',
                    header: 'Qty',
                    cell: item => (item.quantity ?? 0).toFixed(2),
                    width: 80,
                },
                {
                    id: 'actions',
                    header: '',
                    cell: item => (
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="primary" onClick={() => openBuyModal(item)}>Buy</Button>
                            <Button variant="normal" onClick={() => openSellModal(item)}>Sell</Button>
                        </SpaceBetween>
                    ),
                    width: 130,
                },
            ]}
        />
        </SpaceBetween>
    );

    // ---------------------------------------------------------------------------
    // Render — Recent Orders table
    // ---------------------------------------------------------------------------

    const statusIndicatorType = (status: string | undefined): 'success' | 'in-progress' | 'pending' | 'error' | 'stopped' => {
        switch ((status ?? '').toUpperCase()) {
            case 'FILLED': return 'success';
            case 'WORKING':
            case 'PENDING': return 'in-progress';
            case 'QUEUED': return 'pending';
            case 'CANCELLED':
            case 'REJECTED': return 'error';
            default: return 'stopped';
        }
    };

    const renderActiveOrders = () => (
        <Table
            header={
                <Header
                    variant="h2"
                    counter={activeOrders.length > 0 ? `(${activeOrders.length} active)` : undefined}
                    actions={
                        <Button
                            iconName="refresh"
                            variant="icon"
                            ariaLabel="Refresh orders"
                            loading={ordersLoading}
                            onClick={loadOrders}
                        />
                    }
                >
                    Recent Orders
                </Header>
            }
            items={orders}
            loading={ordersLoading}
            loadingText="Loading orders…"
            columnDefinitions={[
                { id: 'ticker', header: 'Ticker', cell: o => o.ticker ?? '—' },
                { id: 'type', header: 'Type', cell: o => o.type ?? '—' },
                {
                    id: 'status',
                    header: 'Status',
                    cell: o => (
                        <StatusIndicator type={statusIndicatorType(o.status)}>
                            {o.status ?? '—'}
                        </StatusIndicator>
                    ),
                },
                { id: 'qty', header: 'Quantity', cell: o => (o.quantity ?? 0).toFixed(4) },
                { id: 'filled', header: 'Filled', cell: o => (o.filledQuantity ?? 0).toFixed(4) },
                {
                    id: 'limitPrice',
                    header: 'Limit Price',
                    cell: o => o.limitPrice ? formatMoney(o.limitPrice, currency) : '—',
                },
                {
                    id: 'createdAt',
                    header: 'Created',
                    cell: o => o.createdAt ? new Date(o.createdAt).toLocaleString() : '—',
                },
                {
                    id: 'actions',
                    header: 'Cancel',
                    cell: o => (
                        <Button
                            variant="icon"
                            iconName="close"
                            ariaLabel="Cancel order"
                            loading={cancellingOrderId === o.id}
                            onClick={() => o.id !== undefined && handleCancelOrder(o.id)}
                        />
                    ),
                },
            ]}
            empty={<Box textAlign="center" color="text-body-secondary">No orders found.</Box>}
        />
    );

    // ---------------------------------------------------------------------------
    // Render — Manual Order Entry
    // ---------------------------------------------------------------------------

    const renderManualOrderEntry = () => {
        // Map each searched instrument to an Autosuggest option keyed on the FULL
        // ticker (e.g. KXIAY_US_EQ) so selection resolves back to the instrument.
        const suggestOptions = instrumentOptions
            .filter(i => !!i.ticker)
            .map(i => ({
                value: i.ticker as string,
                label: `${i.shortName ?? ''}${i.shortName ? ' — ' : ''}${i.name ?? ''}` || (i.ticker as string),
                description: `${i.ticker}${i.currencyCode ? ' · ' + i.currencyCode : ''}${i.type ? ' · ' + i.type : ''}`,
            }));

        const minQty = selectedInstrument?.minTradeQuantity;

        return (
            <Container
                header={
                    <Header variant="h2">Manual Order Entry</Header>
                }
            >
                <SpaceBetween size="m">
                    {manualError && (
                        <Alert type="error" header={manualError} dismissible onDismiss={() => setManualError('')} />
                    )}

                    <Grid
                        gridDefinition={[
                            { colspan: { default: 12, s: 7 } },
                            { colspan: { default: 12, s: 5 } },
                        ]}
                    >
                        <FormField
                            label="Instrument"
                            constraintText={
                                selectedInstrument
                                    ? `Selected: ${selectedInstrument.ticker}${selectedInstrument.currencyCode ? ' · ' + selectedInstrument.currencyCode : ''}`
                                    : 'Type at least 2 characters to search Trading 212 instruments.'
                            }
                        >
                            <Autosuggest
                                value={manualTicker}
                                options={suggestOptions}
                                statusType={instrumentSearchLoading ? 'loading' : 'finished'}
                                loadingText="Searching instruments…"
                                placeholder="Search e.g. AAPL, Tesla, KXIAY"
                                ariaLabel="Instrument search"
                                filteringType="manual"
                                empty="No matching instruments."
                                enteredTextLabel={value => `Use: ${value}`}
                                onChange={({ detail }) => {
                                    setManualTicker(detail.value);
                                    // Typing away from the chosen value clears the selection.
                                    if (selectedInstrument && detail.value !== selectedInstrument.ticker) {
                                        setSelectedInstrument(null);
                                    }
                                    runInstrumentSearch(detail.value);
                                }}
                                onSelect={({ detail }) => {
                                    const inst = instrumentOptions.find(i => i.ticker === detail.value) ?? null;
                                    setSelectedInstrument(inst);
                                    if (inst?.ticker) setManualTicker(inst.ticker);
                                    setManualError('');
                                }}
                            />
                        </FormField>

                        <FormField
                            label="Quantity to buy (shares)"
                            constraintText={
                                minQty != null
                                    ? `Minimum trade quantity: ${minQty}`
                                    : 'Number of shares to buy.'
                            }
                        >
                            <Input
                                type="number"
                                value={manualInput}
                                onChange={({ detail }) => setManualInput(detail.value)}
                                placeholder="e.g. 2.5"
                            />
                        </FormField>
                    </Grid>

                    <SpaceBetween direction="horizontal" size="xs">
                        <Button
                            variant="primary"
                            onClick={handleManualPlaceOrder}
                            loading={orderPlacing}
                            disabled={!selectedInstrument?.ticker || !manualInput.trim()}
                        >
                            Place Market Order
                        </Button>
                    </SpaceBetween>
                </SpaceBetween>
            </Container>
        );
    };

    // ---------------------------------------------------------------------------
    // Render — Buy / Sell position modal
    // ---------------------------------------------------------------------------

    const renderPositionModal = () => {
        if (!positionModal) return null;
        const { position, side } = positionModal;
        const isBuy = side === 'buy';
        // Use account-currency price (GBP) for modal display and qty estimation. The
        // backend nulls this out when it can't produce a reliable GBP price — never fall
        // back to currentPrice (instrument currency), which would mislead the user.
        const price = position.currentPriceInAccountCurrency;
        const hasGbpPrice = isUsableGbpPrice(price);
        const availQty = position.quantityAvailableForTrading ?? 0;

        // Compute quantity for confirmation display. Only possible for by-value entry
        // when we have a trustworthy GBP price.
        let confirmQty: number | null = null;
        if (modalByValue) {
            const val = parseFloat(modalValueInput);
            confirmQty = isNaN(val) || !hasGbpPrice ? null : computeQuantityFromValue(val, price);
        } else {
            const q = parseFloat(modalQuantityInput);
            confirmQty = isNaN(q) ? null : q;
        }

        const estimatedValue = confirmQty !== null && hasGbpPrice ? confirmQty * price : null;

        const isConfirmDisabled =
            orderPlacing ||
            (modalByValue
                ? // By-value entry needs a trustworthy GBP price to size the order.
                  !hasGbpPrice || !modalValueInput.trim() || parseFloat(modalValueInput) <= 0
                : !modalQuantityInput.trim() || parseFloat(modalQuantityInput) <= 0);

        return (
            <Modal
                visible
                onDismiss={closePositionModal}
                size="medium"
                header={isBuy ? `Buy More — ${position.ticker}` : `Sell — ${position.ticker}`}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={closePositionModal} disabled={orderPlacing}>
                                Cancel
                            </Button>
                            <Button
                                variant={isBuy ? 'primary' : 'normal'}
                                onClick={handleConfirmPositionOrder}
                                disabled={isConfirmDisabled}
                                loading={orderPlacing}
                            >
                                {isBuy ? 'Place Buy Order' : 'Place Sell Order'}
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                <SpaceBetween size="m">
                    <Alert type="warning">
                        This order will be sent to Trading 212 immediately. It cannot be cancelled from this app.
                    </Alert>

                    <Box>
                        <strong>Account:</strong> {selectedAccount === 'ina' ? 'Ina' : 'Primary'}
                    </Box>

                    <Grid
                        gridDefinition={[
                            { colspan: { default: 6 } },
                            { colspan: { default: 6 } },
                        ]}
                    >
                        <Box>
                            <Box variant="small" color="text-body-secondary">Current price</Box>
                            <Box fontWeight="bold">{formatMoney(price, currency)}</Box>
                        </Box>
                        {isBuy ? (
                            <Box>
                                <Box variant="small" color="text-body-secondary">Available to trade</Box>
                                <Box fontWeight="bold">{formatMoney(availableToTrade, currency)}</Box>
                            </Box>
                        ) : (
                            <Box>
                                <Box variant="small" color="text-body-secondary">Qty available for trading</Box>
                                <Box fontWeight="bold">{availQty.toFixed(4)}</Box>
                            </Box>
                        )}
                    </Grid>

                    {isBuy ? (
                        <SpaceBetween size="s">
                            <FormField
                                label={modalByValue ? `Value to spend (${currency})` : 'Quantity to buy'}
                                constraintText={
                                    modalByValue && !hasGbpPrice
                                        ? 'No GBP price available — enter a share quantity instead.'
                                        : modalByValue && confirmQty !== null
                                        ? `≈ ${confirmQty.toFixed(4)} shares`
                                        : undefined
                                }
                            >
                                {modalByValue ? (
                                    <Input
                                        type="number"
                                        value={modalValueInput}
                                        onChange={({ detail }) => setModalValueInput(detail.value)}
                                        placeholder={`Amount in ${currency}`}
                                        autoFocus
                                    />
                                ) : (
                                    <Input
                                        type="number"
                                        value={modalQuantityInput}
                                        onChange={({ detail }) => setModalQuantityInput(detail.value)}
                                        placeholder="Quantity"
                                        autoFocus
                                    />
                                )}
                            </FormField>

                            <FormField label="Entry mode">
                                <Toggle
                                    checked={modalByValue}
                                    onChange={({ detail }) => {
                                        setModalByValue(detail.checked);
                                        setModalValueInput('');
                                        setModalQuantityInput('');
                                    }}
                                >
                                    {modalByValue ? `By value (${currency})` : 'By quantity'}
                                </Toggle>
                            </FormField>

                            <Box>
                                <Box variant="small" color="text-body-secondary">Quick fractions of available cash</Box>
                                <SpaceBetween direction="horizontal" size="xs">
                                    {[1 / 4, 1 / 3, 1 / 2].map((f, i) => (
                                        <Button
                                            key={i}
                                            variant="normal"
                                            onClick={() => setModalFraction(f)}
                                        >
                                            {f === 1 / 4 ? '1/4' : f === 1 / 3 ? '1/3' : '1/2'}
                                        </Button>
                                    ))}
                                </SpaceBetween>
                            </Box>
                        </SpaceBetween>
                    ) : (
                        <SpaceBetween size="s">
                            <FormField
                                label={modalByValue ? `Value to sell (${currency})` : 'Quantity to sell'}
                                constraintText={
                                    modalByValue && !hasGbpPrice
                                        ? 'No GBP price available — enter a share quantity instead.'
                                        : modalByValue && confirmQty !== null
                                        ? `≈ ${confirmQty.toFixed(4)} shares`
                                        : !modalByValue && estimatedValue !== null
                                        ? `≈ ${formatMoney(estimatedValue, currency)} at current price`
                                        : undefined
                                }
                            >
                                {modalByValue ? (
                                    <Input
                                        type="number"
                                        value={modalValueInput}
                                        onChange={({ detail }) => setModalValueInput(detail.value)}
                                        placeholder={`Amount in ${currency}`}
                                        autoFocus
                                    />
                                ) : (
                                    <Input
                                        type="number"
                                        value={modalQuantityInput}
                                        onChange={({ detail }) => setModalQuantityInput(detail.value)}
                                        placeholder="e.g. 1.5"
                                        autoFocus
                                    />
                                )}
                            </FormField>

                            <FormField label="Entry mode">
                                <Toggle
                                    checked={modalByValue}
                                    onChange={({ detail }) => {
                                        setModalByValue(detail.checked);
                                        setModalValueInput('');
                                        setModalQuantityInput('');
                                    }}
                                >
                                    {modalByValue ? `By value (${currency})` : 'By quantity'}
                                </Toggle>
                            </FormField>

                            <Box>
                                <Box variant="small" color="text-body-secondary">
                                    {modalByValue ? 'Quick fractions of position value' : 'Quick fractions of available qty'}
                                </Box>
                                <SpaceBetween direction="horizontal" size="xs">
                                    {([1 / 4, 1 / 3, 1 / 2, 1] as number[]).map((f, i) => (
                                        <Button
                                            key={i}
                                            variant="normal"
                                            onClick={() => setModalFraction(f)}
                                        >
                                            {f === 1 / 4 ? '1/4' : f === 1 / 3 ? '1/3' : f === 1 / 2 ? '1/2' : 'All'}
                                        </Button>
                                    ))}
                                </SpaceBetween>
                            </Box>
                        </SpaceBetween>
                    )}

                    {isBuy && modalByValue && modalValueInput ? (
                        <Box>
                            <Box variant="small" color="text-body-secondary">Order value</Box>
                            <Box fontWeight="bold">{formatMoney(parseFloat(modalValueInput), currency)}</Box>
                        </Box>
                    ) : estimatedValue !== null && (
                        <Box>
                            <Box variant="small" color="text-body-secondary">Estimated order value</Box>
                            <Box fontWeight="bold">{formatMoney(estimatedValue, currency)}</Box>
                        </Box>
                    )}
                </SpaceBetween>
            </Modal>
        );
    };

    // ---------------------------------------------------------------------------
    // Render — Manual order confirmation modal
    // ---------------------------------------------------------------------------

    const renderManualConfirmModal = () => {
        if (!manualConfirmVisible || !manualPendingRequest) return null;
        const qty = manualPendingRequest.quantity ?? 0;
        const side = qty >= 0 ? 'Buy' : 'Sell';

        return (
            <Modal
                visible
                onDismiss={() => setManualConfirmVisible(false)}
                size="small"
                header={`Confirm ${side} Order`}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => setManualConfirmVisible(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={handleManualConfirm} loading={orderPlacing}>
                                Confirm
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                <SpaceBetween size="s">
                    <Alert type="warning">
                        This market order will be sent to Trading 212 immediately.
                    </Alert>
                    <Box>
                        <strong>Account:</strong> {selectedAccount === 'ina' ? 'Ina' : 'Primary'}
                    </Box>
                    <Box>
                        <strong>Ticker:</strong> {manualPendingRequest.ticker}
                    </Box>
                    <Box>
                        <strong>Quantity:</strong> {Math.abs(qty).toFixed(4)} ({side})
                    </Box>
                </SpaceBetween>
            </Modal>
        );
    };

    // ---------------------------------------------------------------------------
    // Main render
    // ---------------------------------------------------------------------------

    return (
        <SpaceBetween size="l">
            <Header variant="h1">Trading 212 ISA</Header>

            <Tabs
                activeTabId={selectedAccount}
                onChange={({ detail }) => setSelectedAccount(detail.activeTabId as AccountId)}
                tabs={[
                    { id: 'primary', label: 'Primary' },
                    { id: 'ina',     label: 'Ina' },
                ]}
            />

            {renderAccountSummary()}
            {renderPortfolio()}
            {renderActiveOrders()}
            {renderManualOrderEntry()}

            {renderPositionModal()}
            {renderManualConfirmModal()}
        </SpaceBetween>
    );
};
